import { useState, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import type { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import styled from '@emotion/styled';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { USER_RESOURCES_QUERY_KEY } from '@hotjar/user-resources';
import type { Site } from '@hotjar/site';
import { celebrate } from '@hotjar/webapp/utils/confetti';
import { Text, Link, tokens, TextInput, Field, Button, useNotify } from '@hotjar/hotsauce';
import { validateHTTPSUrl } from '@hotjar/insights-common/lib/url';
import { updateSite } from '@hotjar/site';
import { bus, trackingCodeInstalled } from '@hotjar/webapp/utils/events';
import { getStateFromServer } from '@hotjar/webapp/domains/activation/tracking-code';
import { useTrackingCode } from '@hotjar/webapp/hooks/useTrackingCode';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useHelpHotjarUrl } from '@hotjar/translations';

import { TrackingCodeVerificationResult } from './TrackingCodeVerificationResult';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ScriptWarning = styled.div`
  margin-bottom: ${tokens.spacer.x3};
`;

const StyledField = styled(Field)`
  padding-bottom: ${tokens.spacer.x3};
  width: 100%;

  label {
    text-transform: none;
  }
`;

interface TrackingCodeVerificationTabProps {
  site: Site;
  onSuccessClick: () => void;
}

interface URLTrackingCodeFormData {
  siteUrl: string;
}

export const TrackingCodeVerificationTab = ({
  site,
  onSuccessClick,
}: TrackingCodeVerificationTabProps): JSX.Element | null => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const [url, setUrl] = useState('');
  const [isSettingUrl, setIsSettingUrl] = useState(false);
  const [verificationInProgress, setVerficationInProgress] = useState(false);
  const { getState } = useTrackingCode();
  const [initialTrackingCodeState] = useState(getState(site.lastData));
  const { showToast } = useNotify();
  const { tagRecording } = useTrack();
  const { register, handleSubmit, errors } = useForm<URLTrackingCodeFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { siteUrl: site.url },
  });
  const isAllowingClientScript = !!(window.hj && window.hjSiteSettings);

  const onSubmit = async ({ siteUrl }: URLTrackingCodeFormData) => {
    setUrl(siteUrl);
    setIsSettingUrl(true);

    try {
      await updateSite(site, { url: siteUrl, name: site.name || siteUrl });
      queryClient.invalidateQueries(USER_RESOURCES_QUERY_KEY);
      setIsSettingUrl(false);
      setVerficationInProgress(true);
    } catch (error: unknown) {
      const errorToast = (error as AxiosError).response?.status === 403 ? 'permissions' : 'generic';
      showToast({
        text: t(`activation:tracking-code.verification-tab.request-error.${errorToast}`),
        kind: 'danger',
      });
      setIsSettingUrl(false);
    }
  };

  const onVerificationCheckCompleted = useCallback(async () => {
    const trackingCodeState = await getStateFromServer(site).catch(() => initialTrackingCodeState);
    if (trackingCodeState.installed !== initialTrackingCodeState.installed) {
      // Tracking Code installed for the first time
      tagRecording('TC Verification: Success');
      if (!site.url) {
        updateSite(site, { url, name: site.name || url });
      }
      bus.publish(trackingCodeInstalled({ site }));
      celebrate();
    } else if (!trackingCodeState.installed) {
      tagRecording('TC Verification: Failure');
    }
  }, [site, url, tagRecording, initialTrackingCodeState]);

  if (verificationInProgress) {
    return (
      <TrackingCodeVerificationResult
        site={site}
        url={url}
        onLoad={onVerificationCheckCompleted}
        onErrorButtonClick={() => setVerficationInProgress(false)}
        onSuccessButtonClick={onSuccessClick}
      />
    );
  }

  return (
    <Wrapper>
      <Text style={{ marginBottom: tokens.spacer.x3 }} as="p" textType="subtitle1">
        {t('activation:tracking-code.verification-tab.title')}
      </Text>
      <form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="verify-site-url">
        <StyledField
          isInvalid={!!errors.siteUrl}
          invalidText={errors.siteUrl?.message}
          inputId="siteUrl"
          labelText={t('activation:tracking-code.verification-tab.label')}
          helperText={t('activation:tracking-code.verification-tab.caption')}
        >
          <TextInput
            id="siteUrl"
            name="siteUrl"
            placeholder={t('activation:tracking-code.verification-tab.placeholder')}
            ref={
              register &&
              register({
                required: t('activation:tracking-code.verification-tab.required-error') as string,
                validate: (value: string) => {
                  if (!validateHTTPSUrl(value)) {
                    return t(
                      'activation:tracking-code.verification-tab.invalid-https-url',
                    ) as string;
                  }
                  if (value.length > 250) {
                    return t(
                      'activation:tracking-code.verification-tab.max-length-error',
                    ) as string;
                  }
                  return true;
                },
              })
            }
            isInvalid={!!errors.siteUrl}
            isRequired
          />
        </StyledField>
        {!isAllowingClientScript ? (
          <ScriptWarning data-testid="script-not-allowed-warning">
            <Text textType="body2" textColor="text_error">
              <Trans i18nKey="activation:tracking-code.verification-tab.script-warning">
                {`Uh oh - looks like you're using an ad/script blocker. Temporarily disable it to verify
              your installation.`}
                <Link
                  kind="inline"
                  href={`${helpHotjarUrl}/articles/360016303294-How-to-Verify-Hotjar-is-Installed#ad_block?itm_source=insights&itm_medium=verification_modal&itm_campaign=ad_block_warning&itm_content=learn_more_link`}
                  isExternal
                >
                  Learn more
                </Link>
                .
              </Trans>
            </Text>
          </ScriptWarning>
        ) : null}
        <div>
          <Button isLoading={isSettingUrl} kind="primary" type="submit">
            {t('activation:tracking-code.verification-tab.cta')}
          </Button>
        </div>
      </form>
    </Wrapper>
  );
};
